import { PARKING_ACTION_TYPES } from "../_actionTypes/PARKING_ACTION_TYPES";
import { parkingService } from "../_services/parking.service";
import { invitationGroupService } from "../_services/invitationGroups.service";
import { invitationGroupActions } from "./invitationGroup.actions";
import { InvitationGroupMapper } from "../_mappers/InvitationGroupMapper";
import { ParkingMapper } from "../_mappers/ParkingMapper";
import { ParkingFilter } from "../_models/ParkingSpot";
import { uiAlertActions } from "./uiAlert.actions";
import {
    EXCEPTIONS,
    INVITATION_GROUP_TYPES,
    INVITATION_TYPES,
} from "../_constants";
import { LOCATION_PERMISSIONS } from "../_constants/permissions.constants";

// Creating a parking spot / assignment may create invitations server-side and
// therefore can surface any invitation-related error (e.g. 642 max invitations
// reached, 587 subinvitations without number, group limits, etc). All of these
// arrive as a thrown Error whose `.toString()` is already localized by the
// EverouErrorMapper — show it via the standard UI alert instead of letting it
// bubble up as an uncaught runtime error.
function showInvitationError(dispatch: any, error: any) {
    if (isRequestExpired(error)) return;
    dispatch(uiAlertActions.error(error?.toString?.() ?? String(error)));
}

// RacingCallsManager throws REQUEST_EXPIRED when a newer request to the same
// endpoint supersedes an older in-flight one. These are not real failures and
// must NOT trigger UI alerts or state mutations — the winning call will update
// the store shortly after.
function isRequestExpired(error: any): boolean {
    return error?.message === EXCEPTIONS.REQUEST_EXPIRED;
}

interface ListSpotsParams {
    locationUid: string;
    offset?: number;
    pageSize?: number;
    filter?: ParkingFilter;
}

export const parkingActions = {
    listSpots,
    selectSpot,
    updateSelectedLocally,
    changeFilter,
    changeOffset,
    resetPagination,

    createSpot,
    bulkCreateSpots,
    updateSpot,
    deleteSpot,
    clearAll,
    assignHolder,

    addAssignment,
    removeAssignment,

    loadConfig,
    setInvitationGroup,
    createAndSetGroup,
};

function createAndSetGroup({
    locationUid,
    name,
    accessScope,
    roomUids,
}: {
    locationUid: string;
    name: string;
    accessScope: "LOCATION" | "ROOMS";
    roomUids: string[];
}) {
    return async (dispatch): Promise<{ uid: string; name: string } | null> => {
        try {
            const isLocationScope = accessScope === "LOCATION";
            const serverGroup = await invitationGroupService.createInvitationGroup({
                name,
                cardCode: undefined,
                allowsSubinvitations: true,
                // Placeholder cap on the template. Per-spot creation overrides this
                // with `max_assignments`; server requires a non-null number when
                // allows_subinvitations is true (error 587).
                subinvitations: 1,
                duration: undefined,
                roomUids: isLocationScope ? [] : roomUids,
                locationUid,
                type: isLocationScope
                    ? INVITATION_TYPES.LOCATION
                    : INVITATION_TYPES.ROOM,
                permissionType: LOCATION_PERMISSIONS.GUEST,
                groupType: INVITATION_GROUP_TYPES.UNLIMITED,
                createKeypadCode: false,
            });
            const localGroup = InvitationGroupMapper.serverToLocal(serverGroup);
            await dispatch(invitationGroupActions.getGroupsForLocation(locationUid));
            await dispatch(
                parkingActions.setInvitationGroup({
                    locationUid,
                    invitationGroupUid: localGroup.uid,
                }),
            );
            return { uid: localGroup.uid, name: localGroup.name };
        } catch (error) {
            showInvitationError(dispatch, error);
            return null;
        }
    };
}

function loadConfig(locationUid: string) {
    return async (dispatch) => {
        try {
            const response = await parkingService.getParkingConfig(locationUid);
            dispatch({
                type: PARKING_ACTION_TYPES.PARKING_CONFIG_SUCCESS,
                invitationGroupUid: response.parking_invitation_group_uid ?? null,
            });
        } catch (error) {
            // A superseded request is not a real failure — the winning call
            // will update the store. Leaving `configLoaded` false avoids
            // flashing the "not configured" banner before the real result
            // arrives.
            if (isRequestExpired(error)) return;
            // Mark as loaded even on real failure so the UI can show the
            // "not configured" banner (instead of staying in a loading
            // limbo forever when the endpoint breaks).
            dispatch({
                type: PARKING_ACTION_TYPES.PARKING_CONFIG_SUCCESS,
                invitationGroupUid: null,
            });
            dispatch(uiAlertActions.error(
                (error as any)?.toString?.() ?? String(error),
            ));
        }
    };
}

function setInvitationGroup({
    locationUid,
    invitationGroupUid,
}: {
    locationUid: string;
    invitationGroupUid: string | null;
}) {
    return async (dispatch) => {
        await parkingService.setParkingConfig({
            loc_uid: locationUid,
            parking_invitation_group_uid: invitationGroupUid,
        });
        dispatch({
            type: PARKING_ACTION_TYPES.PARKING_CONFIG_SUCCESS,
            invitationGroupUid,
        });
    };
}

interface AddAssignmentResult {
    ok: boolean;
    error?: 'DUPLICATE' | 'LIMIT_REACHED' | 'TEMP_NOT_APPLICABLE' | 'UNKNOWN';
    limit?: number;
}

function addAssignment({
    spotUid,
    email,
    userName,
    begin,
    end,
}: {
    spotUid: string;
    email: string;
    userName?: string;
    begin?: string | null;
    end?: string | null;
}) {
    return async (dispatch): Promise<AddAssignmentResult> => {
        try {
            const response = await parkingService.addSpotAssignment({
                uid: spotUid,
                email,
                user_name: userName,
                begin,
                end,
            });
            dispatch({
                type: PARKING_ACTION_TYPES.PARKING_UPDATE_SUCCESS,
                spot: ParkingMapper.mapServerToLocal(response),
            });
            return { ok: true };
        } catch (e: any) {
            // ServiceHelper surfaces backend `error_code` as `error.name`
            // (numeric). Use the code, not the info string.
            if (e?.name === 757) {
                return { ok: false, error: 'DUPLICATE' };
            }
            if (e?.name === 758) {
                return { ok: false, error: 'LIMIT_REACHED', limit: e.limit };
            }
            if (e?.name === 767) {
                return { ok: false, error: 'TEMP_NOT_APPLICABLE' };
            }
            // Other invitation-related errors (e.g. 642 max invitations) — show
            // the localized message instead of letting it bubble up uncaught.
            showInvitationError(dispatch, e);
            return { ok: false, error: 'UNKNOWN' };
        }
    };
}

function removeAssignment({
    spotUid,
    email,
}: {
    spotUid: string;
    email: string;
}) {
    return async (dispatch) => {
        const response = await parkingService.removeSpotAssignment({
            uid: spotUid,
            email,
        });
        dispatch({
            type: PARKING_ACTION_TYPES.PARKING_UPDATE_SUCCESS,
            spot: ParkingMapper.mapServerToLocal(response),
        });
    };
}

function clearAll(locationUid: string) {
    return async (dispatch, getState) => {
        await parkingService.clearAllParkingSpots(locationUid);
        const state = getState();
        const filter = state.everou?.parking?.filter;
        const pageSize = state.everou?.parking?.page_size;
        dispatch(resetPagination());
        dispatch(listSpots({ locationUid, offset: 0, pageSize, filter }));
    };
}

interface CreateSpotPayload {
    locationUid: string;
    floor: string;
    numberRaw: string;
    holderName?: string;
    holderEmail?: string;
    maxAssignments?: number;
    notes?: string;
}

function createSpot(payload: CreateSpotPayload) {
    return async (dispatch, getState): Promise<boolean> => {
        try {
            await parkingService.createParkingSpot({
                loc_uid: payload.locationUid,
                floor: payload.floor,
                number_raw: payload.numberRaw,
                holder_name: payload.holderName,
                holder_email: payload.holderEmail,
                max_assignments: payload.maxAssignments,
                notes: payload.notes,
            });
        } catch (error) {
            showInvitationError(dispatch, error);
            return false;
        }
        // Refresh current page with current filter/pagination
        const state = getState();
        const filter = state.everou?.parking?.filter;
        const offset = state.everou?.parking?.offset ?? 0;
        const pageSize = state.everou?.parking?.page_size;
        dispatch(
            listSpots({
                locationUid: payload.locationUid,
                offset,
                pageSize,
                filter,
            }),
        );
        return true;
    };
}

function listSpots({ locationUid, offset, pageSize, filter }: ListSpotsParams) {
    return async (dispatch) => {
        dispatch({ type: PARKING_ACTION_TYPES.PARKING_LIST_REQUEST });
        try {
            const response = await parkingService.getParkingSpots({
                loc_uid: locationUid,
                offset,
                page_size: pageSize,
                floor: filter?.floor ?? null,
                status: filter?.status,
                search: filter?.searchTerm,
            });
            const items = ParkingMapper.mapAllServerToLocal(response.items);
            dispatch({
                type: PARKING_ACTION_TYPES.PARKING_LIST_SUCCESS,
                items,
                offset: response.offset,
                page_size: response.page_size,
                total_count: response.total_count,
                floors: response.floors,
            });
        } catch (error) {
            // Superseded by a newer list request — let the winner update
            // the store; do not drop the spinner or show an alert.
            if (isRequestExpired(error)) return;
            dispatch({ type: PARKING_ACTION_TYPES.PARKING_LIST_FAILURE });
            dispatch(uiAlertActions.error(
                (error as any)?.toString?.() ?? String(error),
            ));
        }
    };
}

function selectSpot(spotUid: string | null) {
    return {
        type: PARKING_ACTION_TYPES.PARKING_SELECT,
        spotUid,
    };
}

function updateSelectedLocally(patch: any) {
    return {
        type: PARKING_ACTION_TYPES.PARKING_UPDATE_SELECTED_LOCALLY,
        patch,
    };
}

function changeFilter(filter: ParkingFilter) {
    return {
        type: PARKING_ACTION_TYPES.PARKING_CHANGE_FILTER,
        filter,
    };
}

function changeOffset(offset: number) {
    return {
        type: PARKING_ACTION_TYPES.PARKING_CHANGE_OFFSET,
        offset,
    };
}

function resetPagination() {
    return { type: PARKING_ACTION_TYPES.PARKING_RESET_PAGINATION };
}

function assignHolder({
    spotUid,
    userUid,
    userName,
    userEmail,
    invitationUid,
}: {
    spotUid: string;
    userUid: string;
    userName: string;
    userEmail: string;
    invitationUid: string;
}) {
    return async (dispatch): Promise<boolean> => {
        try {
            const response = await parkingService.assignHolder({
                uid: spotUid,
                holder_user_uid: userUid,
                holder_name: userName,
                holder_email: userEmail,
                holder_invitation_uid: invitationUid,
            });
            dispatch({
                type: PARKING_ACTION_TYPES.PARKING_UPDATE_SUCCESS,
                spot: ParkingMapper.mapServerToLocal(response),
            });
            return true;
        } catch (error) {
            showInvitationError(dispatch, error);
            return false;
        }
    };
}

interface BulkCreateSpotInput {
    holder: string;
    email: string;
    planta: string;
    numero: string;
    maxAssignments?: number;
}

export interface BulkCreateRowIssue {
    row_index: number;
    code: string;
    message: string;
}

export interface BulkCreateResult {
    created: number;
    reused: number;
    skipped: number;
    errors: number;
    error_rows?: BulkCreateRowIssue[];
    warning_rows?: BulkCreateRowIssue[];
}

function bulkCreateSpots({
    locationUid,
    rows,
}: {
    locationUid: string;
    rows: BulkCreateSpotInput[];
}) {
    return async (dispatch, getState): Promise<BulkCreateResult> => {
        let result: BulkCreateResult;
        try {
            result = await parkingService.bulkCreateParkingSpots({
                loc_uid: locationUid,
                rows: rows.map((row) => ({
                    holder_name: row.holder.trim() || undefined,
                    holder_email: row.email.trim() || undefined,
                    floor: row.planta,
                    number_raw: row.numero,
                    max_assignments: row.maxAssignments,
                })),
            });
        } catch (error) {
            showInvitationError(dispatch, error);
            return { created: 0, reused: 0, skipped: 0, errors: rows.length };
        }

        // Refresh current page with existing filter/pagination
        const state = getState();
        const filter = state.everou?.parking?.filter;
        const offset = state.everou?.parking?.offset ?? 0;
        const pageSize = state.everou?.parking?.page_size;
        dispatch(
            listSpots({ locationUid, offset, pageSize, filter }),
        );

        return result;
    };
}

function updateSpot({
    spotUid,
    holderName,
    holderEmail,
    maxAssignments,
    notes,
}: {
    spotUid: string;
    holderName?: string | null;
    holderEmail?: string | null;
    maxAssignments?: number;
    notes?: string;
}) {
    return async (dispatch): Promise<boolean> => {
        const name = holderName?.trim() ?? null;
        const email = holderEmail?.trim() ?? null;
        // The email is the holder's identity on the backend; the name is
        // optional display info. Presence of the email (not the name) is what
        // keeps the spot assigned. Clearing both frees the spot.
        const hasHolder = !!email;
        try {
            const response = await parkingService.updateParkingSpot({
                uid: spotUid,
                holder_name: hasHolder ? name : null,
                holder_email: hasHolder ? email : null,
                max_assignments: maxAssignments,
                notes: notes ?? '',
            });
            dispatch({
                type: PARKING_ACTION_TYPES.PARKING_UPDATE_SUCCESS,
                spot: ParkingMapper.mapServerToLocal(response),
            });
            return true;
        } catch (error) {
            showInvitationError(dispatch, error);
            return false;
        }
    };
}

function deleteSpot(spotUid: string) {
    return async (dispatch) => {
        await parkingService.deleteParkingSpot(spotUid);
        dispatch({
            type: PARKING_ACTION_TYPES.PARKING_DELETE_SUCCESS,
            uid: spotUid,
        });
    };
}
